import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DeviceType } from 'src/constants/DeviceTypes';
import { selectFacilityId } from 'src/store/configSlice';
import {
    AllocationType,
    DeviceAllocation,
    useGetAllocatedDevicesQuery,
} from 'src/store/sageAdminApi';

export const InovonicsAllocationTypes = new Set<AllocationType>(['BUTTON_WITH_RESET', 'PULL_CORD']);

interface DeviceLastPress {
    deviceTypes?: DeviceType[];
    queryOptions?: {
        pollingInterval?: number;
        refetchOnMountOrArgChange?: boolean;
        refetchOnReconnect?: boolean;
    };
}

function useFacilityDeviceAllocations(props?: DeviceLastPress) {
    const { deviceTypes = [], queryOptions = {} } = props || {};
    const { pollingInterval, refetchOnMountOrArgChange, refetchOnReconnect } = queryOptions;
    const facilityId = useSelector(selectFacilityId);

    // Get all allocated devices in the facility
    const { data: allAllocations = [], ...rest } = useGetAllocatedDevicesQuery(
        { facilityRid: facilityId, deviceTypes },
        { skip: !facilityId, pollingInterval, refetchOnMountOrArgChange, refetchOnReconnect }
    );

    const {
        unassignedAllocations,
        residentAllocations,
        unitAllocations,
        doorAllocations,
        idToAllocationMap,
        deviceIdToAllocationMap,
    } = useMemo(
        () =>
            allAllocations.reduce(
                (acc, allocation: DeviceAllocation) => {
                    if (allocation.resident) {
                        acc.residentAllocations.push(allocation);
                    } else if (allocation.unit) {
                        acc.unitAllocations.push(allocation);
                    } else if (allocation.door) {
                        acc.doorAllocations.push(allocation);
                    } else {
                        acc.unassignedAllocations.push(allocation);
                    }

                    if (allocation.id) {
                        acc.idToAllocationMap.set(allocation.id, allocation);
                    }

                    allocation.devices?.forEach((device) => {
                        if (device?.id) {
                            acc.deviceIdToAllocationMap.set(device.id, allocation);
                        }
                    });

                    return acc;
                },
                {
                    unassignedAllocations: [],
                    residentAllocations: [],
                    unitAllocations: [],
                    doorAllocations: [],
                    idToAllocationMap: new Map<string, DeviceAllocation>(),
                    deviceIdToAllocationMap: new Map<string, DeviceAllocation>(),
                } as {
                    unassignedAllocations: DeviceAllocation[];
                    residentAllocations: DeviceAllocation[];
                    unitAllocations: DeviceAllocation[];
                    doorAllocations: DeviceAllocation[];
                    idToAllocationMap: Map<string, DeviceAllocation>;
                    deviceIdToAllocationMap: Map<string, DeviceAllocation>;
                }
            ),
        [allAllocations]
    );

    const hasM3s = useMemo(
        () => allAllocations.some((allocation) => allocation.device?.type === 'Melten M3 Device'),
        [allAllocations]
    );

    const hasFallDetectionDevices = useMemo(
        () =>
            allAllocations.some(
                (allocation) => allocation.device?.type === 'Fall Detection Device'
            ),
        [allAllocations]
    );

    const hasButtonAlertingDevices = useMemo(
        () =>
            allAllocations.some(
                (allocation) =>
                    allocation.allocationType === 'BUTTON' ||
                    allocation.allocationType === 'UNIVERSAL_TRANSMITTER_BUTTON'
            ),
        [allAllocations]
    );

    const hasInovonicsDevices = useMemo(
        () =>
            allAllocations.some(
                (allocation) =>
                    !!allocation.allocationType &&
                    InovonicsAllocationTypes.has(allocation.allocationType)
            ),
        [allAllocations]
    );

    const hasDoors = doorAllocations.length > 0;

    const hasMultipleAlertingDeviceTypes =
        [hasButtonAlertingDevices, hasDoors, hasFallDetectionDevices, hasM3s].filter(Boolean)
            .length > 1;

    return {
        ...rest,
        allAllocations,
        unassignedAllocations,
        residentAllocations,
        unitAllocations,
        doorAllocations,
        idToAllocationMap,
        deviceIdToAllocationMap,

        // Flags
        hasDoors,
        hasButtonAlertingDevices,
        hasFallDetectionDevices,
        hasInovonicsDevices,
        hasM3s,
        hasMultipleAlertingDeviceTypes,
    };
}

export default useFacilityDeviceAllocations;
